import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiTrendingUp, FiDownload, FiPlay, FiFileText, FiHeart, FiThumbsUp, FiClock } = FiIcons;

const TrustReviews = () => {
  const trustScore = 94;
  
  const reviewStats = [
    { label: 'Total Reviews', value: '127', icon: FiStar },
    { label: 'Average Rating', value: '4.8', icon: FiTrendingUp },
    { label: 'Response Rate', value: '98%', icon: FiHeart },
    { label: 'Recent Reviews', value: '12', icon: FiClock },
  ];

  const topPraises = [
    { tag: 'Punctuality', count: 45, color: 'trust' },
    { tag: 'Quality Work', count: 38, color: 'olive' },
    { tag: 'Friendly Service', count: 32, color: 'sand' },
    { tag: 'Fair Pricing', count: 28, color: 'trust' },
    { tag: 'Reliability', count: 25, color: 'olive' },
  ];

  const improvements = [
    { area: 'Communication', feedback: 'Some clients would like more frequent updates', priority: 'Medium' },
    { area: 'Scheduling', feedback: 'Weekend availability requests increasing', priority: 'High' },
    { area: 'Service Range', feedback: 'Requests for additional cleaning services', priority: 'Low' },
  ];

  const voiceFeedback = [
    {
      id: 1,
      client: 'Sarah M.',
      service: 'Home Cleaning',
      rating: 5,
      duration: '2:34',
      sentiment: 'Very Positive',
      transcript: 'Amina did an absolutely fantastic job cleaning my home. She was punctual, thorough, and very professional...',
      date: '2024-01-12'
    },
    {
      id: 2,
      client: 'Mike C.',
      service: 'Garden Maintenance',
      rating: 5,
      duration: '1:48',
      sentiment: 'Positive',
      transcript: 'Great work on the garden. Very satisfied with the results and the attention to detail...',
      date: '2024-01-10'
    },
    {
      id: 3,
      client: 'Lisa R.',
      service: 'Pet Sitting',
      rating: 4,
      duration: '3:12',
      sentiment: 'Positive',
      transcript: 'Amina took great care of my pets. They seemed happy and comfortable with her...',
      date: '2024-01-08'
    }
  ];

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'Very Positive': return 'bg-green-100 text-green-800';
      case 'Positive': return 'bg-blue-100 text-blue-800';
      case 'Neutral': return 'bg-yellow-100 text-yellow-800';
      case 'Negative': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <motion.div 
        className="flex items-center justify-between"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Trust & Reviews</h1>
          <p className="text-slate-600 mt-1">Your reputation, visualized</p>
        </div>
        
        <motion.button
          className="flex items-center gap-2 bg-trust-500 text-white px-6 py-3 rounded-lg hover:bg-trust-600 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <SafeIcon icon={FiDownload} />
          Download Testimonials
        </motion.button>
      </motion.div>

      {/* Trust Score */}
      <motion.div 
        className="bg-gradient-to-r from-trust-500 to-trust-600 rounded-2xl p-8 text-white"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Your Trust Score</h2>
            <p className="text-trust-100">Based on client reviews, ratings, and feedback</p>
          </div>
          <div className="text-center">
            <div className="text-6xl font-bold mb-2">{trustScore}</div>
            <div className="text-trust-100">out of 100</div>
          </div>
        </div>
      </motion.div>

      {/* Review Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {reviewStats.map((stat, index) => (
          <motion.div
            key={stat.label}
            className="bg-white rounded-xl p-6 shadow-sm border border-slate-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-trust-100">
                <SafeIcon icon={stat.icon} className="text-xl text-trust-600" />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-800 mb-1">{stat.value}</div>
            <div className="text-slate-600 text-sm">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Top Praises */}
      <motion.div 
        className="bg-white rounded-xl p-6 border border-slate-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <h2 className="text-xl font-bold text-slate-800 mb-4">Top Praises</h2>
        <div className="flex flex-wrap gap-3">
          {topPraises.map((praise, index) => (
            <motion.div
              key={praise.tag}
              className={`flex items-center gap-2 px-4 py-2 rounded-full bg-${praise.color}-100 text-${praise.color}-800`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <SafeIcon icon={FiThumbsUp} className="text-sm" />
              <span className="font-medium">{praise.tag}</span>
              <span className="bg-white/50 px-2 py-1 rounded-full text-xs">{praise.count}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Areas for Improvement */}
      <motion.div 
        className="bg-white rounded-xl p-6 border border-slate-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <h2 className="text-xl font-bold text-slate-800 mb-4">Areas for Improvement</h2>
        <div className="space-y-4">
          {improvements.map((item, index) => (
            <motion.div
              key={item.area}
              className="p-4 bg-slate-50 rounded-lg"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-800">{item.area}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  item.priority === 'High' ? 'bg-red-100 text-red-800' :
                  item.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {item.priority} Priority
                </span>
              </div>
              <p className="text-slate-600 text-sm">{item.feedback}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Voice Feedback */}
      <motion.div 
        className="bg-white rounded-xl p-6 border border-slate-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7 }}
      >
        <h2 className="text-xl font-bold text-slate-800 mb-4">Voice Feedback Insights</h2>
        <div className="space-y-4">
          {voiceFeedback.map((feedback, index) => (
            <motion.div
              key={feedback.id}
              className="p-4 border border-slate-200 rounded-lg hover:shadow-md transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-trust-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {feedback.client.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{feedback.client}</h3>
                    <p className="text-slate-600 text-sm">{feedback.service} • {feedback.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <SafeIcon 
                        key={i}
                        icon={FiStar} 
                        className={`text-sm ${i < feedback.rating ? 'text-yellow-500' : 'text-slate-300'}`}
                      />
                    ))}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSentimentColor(feedback.sentiment)}`}>
                    {feedback.sentiment}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-4 mb-3">
                <motion.button
                  className="flex items-center gap-2 bg-trust-100 text-trust-600 px-3 py-2 rounded-lg hover:bg-trust-200 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <SafeIcon icon={FiPlay} />
                  Play ({feedback.duration})
                </motion.button>
                <motion.button
                  className="flex items-center gap-2 bg-slate-100 text-slate-600 px-3 py-2 rounded-lg hover:bg-slate-200 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <SafeIcon icon={FiFileText} />
                  View Transcript
                </motion.button>
              </div>
              
              <p className="text-slate-600 text-sm italic">"{feedback.transcript}"</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default TrustReviews;